class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);

    // Stores element that opened the drawer (for focus restore after close)
    this.openerEl = null;
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener("cart-drawer:refresh", (e) =>
        this.refreshCartDrawer(e),
      );
      return;
    }

    this.init();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton().focus();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  rememberOpener(e) {
    // Prefer currentTarget (the element with listener), fallback to activeElement
    const current = e?.currentTarget;
    const active = document.activeElement;

    // If click landed on svg/span inside button, climb up to interactive parent
    const interactive =
      current instanceof HTMLElement
        ? current.closest?.("button, a, [tabindex], input, select") || current
        : null;

    this.openerEl =
      interactive instanceof HTMLElement
        ? interactive
        : active instanceof HTMLElement
          ? active
          : null;
  }

  restoreFocusToOpener() {
    const el = this.openerEl;
    if (!el) return;
    if (!document.contains(el)) return;

    const isDisabled =
      el.hasAttribute("disabled") ||
      el.getAttribute("aria-disabled") === "true";
    if (isDisabled) return;

    // Wait one frame so DOM/classes settle after closing
    requestAnimationFrame(() => {
      el.focus?.();
    });
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      // this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");

      // Restore focus to the element that opened the drawer
      this.restoreFocusToOpener();
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);

    // dispatch a custom event on the document
    const eventName = this.isOpen
      ? PUB_SUB_EVENTS.cartDrawerOpen
      : PUB_SUB_EVENTS.cartDrawerClose;

    document.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
      }),
    );
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    // Cart-trigger / back-link / overlay clicks are handled by a single
    // document-level capture-phase listener (see below, outside this class).
    // That listener wins over third-party listeners attached to the trigger
    // element itself (e.g. Rebuy SmartCart's capture-phase click handler)
    // and is immune to DOM replacement of the trigger.

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

    document.addEventListener("cart-drawer:refresh", (e) =>
      this.refreshCartDrawer(e),
    );
  }

  renderContents(parsedState, isClosedCart = true) {
    /*
      Capture cart-cross-sell expanded state BEFORE we replace the
      drawer's innerHTML. The pdp-accessory-slider in the cart renders
      with `initially_collapsed: true` so opening the drawer for the
      first time doesn't expose a tall upsell strip. But if the user
      has already expanded it AND is mid-interaction (e.g. just tapped
      ADD on an upsell card), collapsing it back is jarring — the
      slider is the thing they were just using. Stash the flag here
      and re-apply it after the new markup lands so the slider stays
      open across the add.
    */
    const prevSlider = document.querySelector(
      ".wt-cart__cross-sell .wt-pdp-accessory-slider",
    );
    const crossSellWasExpanded =
      !!prevSlider &&
      !prevSlider.classList.contains("wt-pdp-accessory-slider--collapsed");

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
      /*
        Setting `innerHTML` inserts <script> nodes into the DOM but the
        browser does NOT execute them (this is a security/perf rule —
        scripts injected via innerHTML stay inert). That means any
        inline <script> in the re-rendered cart sections — chevron
        toggle binding in pdp-accessory-slider.liquid, wrapper click
        handler in cart-cross-sell.liquid, swiper autoHeight patch,
        etc. — silently dies on every cart mutation, leaving the
        chevron looking active but unclickable.

        Walk the new DOM and clone each <script> into a fresh element.
        Inserting that fresh element runs the script normally,
        identical to how the browser executes scripts parsed from
        initial HTML.         The original inline scripts already guard
        themselves against double-binding (data-pdpAccBound,
        __pdpAccActiveHeightPatched, etc.) so re-execution per render
        cycle is safe.
      */
      this.reexecuteInlineScripts(sectionElement);
    });

    if (crossSellWasExpanded) {
      const newSlider = document.querySelector(
        ".wt-cart__cross-sell .wt-pdp-accessory-slider",
      );
      if (newSlider) {
        newSlider.classList.remove("wt-pdp-accessory-slider--collapsed");
        const toggle = newSlider.querySelector(".js-pdp-acc-collapse-toggle");
        if (toggle) toggle.setAttribute("aria-expanded", "true");
        /*
          Swiper measures slide widths and pagination off the visible
          layout. The new markup just landed and the collapsible region
          starts at max-height: 0 in CSS — popping the `--collapsed`
          class flips it open, but Swiper's measurements are taken
          before that height transition completes. Nudge it to re-
          measure once the new <slideshow-section> has had a tick to
          mount.
        */
        setTimeout(() => {
          const host = newSlider.querySelector(
            "slideshow-section.wt-slider--pdp-accessory",
          );
          if (host && host.swiper) {
            try {
              host.swiper.update();
            } catch (e) {
              // swiper may be mid-destroy on rapid re-renders; non-fatal.
            }
          }
        }, 50);
      }
    }

    if (isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) {
          this.closeButton().focus();
        }
      });
    }
  }

  reexecuteInlineScripts(root) {
    root.querySelectorAll("script").forEach((oldScript) => {
      const newScript = document.createElement("script");
      for (const attr of oldScript.attributes) {
        newScript.setAttribute(attr.name, attr.value);
      }
      newScript.text = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer(e) {
    const sectionsToRender = this.getSectionsToRender();
    fetch(
      `${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`,
    )
      .then((response) => response.json())
      .then((response) => {
        const parsedState = {
          sections: response,
        };
        this.renderContents(parsedState, false);
      })
      .catch((e) => {
        console.log(e);
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

/*
  Document-level, capture-phase delegated click handler for the cart drawer.

  Why this lives outside the class:
  - Capture-phase listeners on `document` run BEFORE any listener attached to
    the trigger element itself (incl. third-party apps like Rebuy SmartCart
    which attach a capture-phase listener directly on `.wt-cart__trigger`).
    By intercepting here and calling `stopImmediatePropagation`, the theme's
    cart drawer always wins for these triggers.
  - It's also immune to DOM replacement of the trigger (header re-render,
    cart-icon-bubble section refresh, etc.) since we never hold a reference
    to a specific node.

  The same handler covers the three cart-related triggers the cart-drawer
  used to bind individually: `.wt-cart__trigger` (header cart icon),
  `.wt-cart__back-link` (in-drawer back link), and `.page-overlay-cart`
  (overlay behind the open drawer).
*/
document.addEventListener(
  "click",
  (e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;
    const trigger = target.closest(
      ".wt-cart__trigger, .wt-cart__back-link, .page-overlay-cart",
    );
    if (!trigger) return;

    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer) return;

    if (cartDrawer.cartType === "page" || cartDrawer.isCartPage) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    if (!cartDrawer.isOpen) {
      cartDrawer.rememberOpener({ currentTarget: trigger });
    }
    cartDrawer.toggleDrawerClasses();
  },
  true,
);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
